/**
 * RunningProgressPanel — connected wrapper that subscribes to the live SSE
 * events stream for a research run and derives the minimal live-card props
 * for the pure {@link RunningProgressPanelView}: plan (todos), researcher
 * activity count, finding count, plan-based progress.
 *
 * Intermediate findings are deliberately NOT rendered live — the card shows
 * the plan and counters only; findings live in the run detail (sheet/page).
 */

import { useMemo } from 'react'
import { RunningProgressPanelView } from '@/components/hypotheses/RunningProgressPanelView'
import { coercePlanTodos } from '@/components/hypotheses/RunPlanList'
import type { HypothesisRunTodo } from '@/types/hypothesis-finding'
import { useHypothesisRunEvents } from './hooks/use-hypothesis-findings'

interface Props {
  caseId: string
  hypothesisId: string
  runId: string
  /** Run start time (ISO) — drives the elapsed timer in the header. */
  startedAt?: string | null
}

export function RunningProgressPanel({ caseId, hypothesisId, runId, startedAt }: Props) {
  const { events, isConnected } = useHypothesisRunEvents(caseId, hypothesisId, runId)

  // Live plan: the agent maintains it via write_todos; the runtime publishes
  // each update as a `plan` event (and the SSE endpoint replays the latest
  // one on connect), so only the last event matters.
  const todos = useMemo<HypothesisRunTodo[]>(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]
      if (e.type !== 'plan') continue
      return coercePlanTodos((e as { todos?: unknown }).todos)
    }
    return []
  }, [events])

  const researcherCallCount = useMemo(
    () => events.filter(e => e.type === 'subagent_activity').length,
    [events]
  )

  const findingCount = useMemo(() => {
    const seen = new Set<string>()
    for (const e of events) {
      if (e.type === 'finding_emerged') {
        seen.add((e as { finding: { id: string } }).finding.id)
      }
    }
    return seen.size
  }, [events])

  return (
    <RunningProgressPanelView
      isConnected={isConnected}
      startedAt={startedAt ?? null}
      todos={todos}
      researcherCallCount={researcherCallCount}
      findingCount={findingCount}
    />
  )
}
